import { Card, Tag, Text, Title } from '@bancobarigui/barihaus';
import { Spin, Timeline } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import { UnifiedHistoryItem } from '@/services/inspection.service';

interface HistoryTimelineProps {
  historyItems: UnifiedHistoryItem[];
  loading: boolean;
  createdAt: string;
  fallbackWorkflow?: any;
}

export function HistoryTimeline({
  historyItems,
  loading,
  createdAt,
  fallbackWorkflow,
}: HistoryTimelineProps) {
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const buildTimelineItems = () => {
    const sortedItems = historyItems.length > 0
      ? [...historyItems].reverse()
      : [];

    if (sortedItems.length > 0) {
      return sortedItems.map((item, index) => {
        const isReportAction = item.source === 'report';
        const isFirst = index === 0;
        const isLast = index === sortedItems.length - 1;

        return {
          color: item.statusColor || '#999',
          children: (
            <div
              style={{
                paddingBottom: '16px',
                marginBottom: '8px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                <Text strong style={{ fontSize: '14px' }}>
                  {item.action}
                </Text>
                {item.statusBadge && (
                  <Tag
                    color={item.statusColor || 'default'}
                    style={{ fontSize: '11px', padding: '1px 6px', lineHeight: '18px', margin: 0 }}
                  >
                    {item.statusBadge}
                  </Tag>
                )}
                {isReportAction && (
                  <Tag color="purple" style={{ fontSize: '11px', padding: '1px 6px', lineHeight: '18px', margin: 0 }}>
                    LAUDO
                  </Tag>
                )}
                {isFirst && (
                  <Tag color="green" style={{ fontSize: '11px', padding: '1px 6px', lineHeight: '18px', margin: 0 }}>
                    INÍCIO
                  </Tag>
                )}
                {isLast && (
                  <Tag color="blue" style={{ fontSize: '11px', padding: '1px 6px', lineHeight: '18px', margin: 0 }}>
                    ATUAL
                  </Tag>
                )}
              </div>
              <div style={{ fontSize: '13px', color: '#555', marginBottom: '4px' }}>
                <span style={{ fontWeight: 500 }}>{item.performedByUserName}</span>
                {item.performedByUserRole && item.performedByUserRole !== item.performedByUserName && (
                  <span style={{ color: '#888' }}> ({item.performedByUserRole})</span>
                )}
                <span style={{ color: '#888' }}> • </span>
                <span style={{ color: '#888' }}>
                  {new Date(item.performedAt).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              {item.targetProviderName && (
                <div style={{ fontSize: '13px', color: '#1890ff', marginBottom: '2px' }}>
                  Prestadora: {item.targetProviderName}
                </div>
              )}
              {item.targetInspectorName && (
                <div style={{ fontSize: '13px', color: '#52c41a', marginBottom: '2px' }}>
                  Inspetor: {item.targetInspectorName}
                </div>
              )}
              {item.notes && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#888',
                    fontStyle: 'italic',
                    marginTop: '4px',
                    whiteSpace: 'pre-line',
                    lineHeight: '1.4'
                  }}
                >
                  {item.notes}
                </div>
              )}
            </div>
          ),
        };
      });
    }

    // Fallback para workflow antigo
    if (fallbackWorkflow?.statusWorkflow && fallbackWorkflow.statusWorkflow.length > 0) {
      return fallbackWorkflow.statusWorkflow.map((status: any) => ({
        color: 'blue',
        children: (
          <div style={{ paddingBottom: '16px' }}>
            <Text strong style={{ fontSize: '14px' }}>{status.action}</Text>
            <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>
              {status.performedByUserName} • {formatDate(status.occurredAt)}
            </div>
            {status.notes && (
              <div style={{ fontSize: '12px', color: '#888', fontStyle: 'italic', marginTop: '4px' }}>{status.notes}</div>
            )}
          </div>
        ),
      }));
    }

    return [{
      color: 'green',
      children: (
        <div style={{ paddingBottom: '16px' }}>
          <Text strong style={{ fontSize: '14px' }}>Inspeção Criada</Text>
          <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>{formatDate(createdAt)}</div>
        </div>
      ),
    }];
  };

  return (
    <Card className="shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <Title level={4} className="mb-0">
          <HistoryOutlined className="mr-2" />
          Histórico de Movimentações
        </Title>
        {historyItems.length > 0 && (
          <Text className="text-gray-500 text-xs">
            {historyItems.length} registro{historyItems.length !== 1 ? 's' : ''}
          </Text>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spin tip="Carregando histórico..." />
        </div>
      ) : (
        <div style={{ maxHeight: '450px', overflowY: 'auto', paddingTop: '8px' }}>
          <Timeline items={buildTimelineItems()} />
        </div>
      )}
    </Card>
  );
}
